import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Folder, FileText, Download, CheckCircle, AlertTriangle, File, Lock } from 'lucide-react';

// --- MOCK API DATA ---
const mockDocuments = [
  { id: 1, name: 'Offer Letter - 2024', category: 'Personal Records', type: 'PDF', date: '2024-03-10', required: false, status: 'Archived' },
  { id: 2, name: 'Employee Handbook v4.0', category: 'Company Policies', type: 'PDF', date: '2024-01-01', required: false, status: 'Active' },
  { id: 3, name: 'W-2 Tax Form 2023', category: 'Tax Documents', type: 'PDF', date: '2024-02-15', required: true, status: 'Signed' },
  { id: 4, name: 'Direct Deposit Form', category: 'Personal Records', type: 'PDF', date: '2024-03-10', required: true, status: 'Pending Signature' },
  { id: 5, name: 'Code of Conduct Policy', category: 'Company Policies', type: 'PDF', date: '2023-11-01', required: true, status: 'Acknowledgement Due' },
];

// --- API MOCK FUNCTION ---
const fetchDocuments = async () => new Promise(resolve => {
    setTimeout(() => resolve({ data: mockDocuments }), 800);
});

// --- FRAMER MOTION VARIANTS ---
const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

// --- UTILITY: Get Document Icon/Color ---
const getFileIcon = (doc) => {
    switch (doc.type) {
        case 'PDF':
            return { Icon: FileText, color: 'text-red-500 bg-red-100' };
        case 'DOCX':
            return { Icon: File, color: 'text-blue-500 bg-blue-100' };
        default:
            return { Icon: File, color: 'text-gray-500 bg-gray-100' };
    }
};

// --- UTILITY COMPONENT: DocumentCard ---
const DocumentCard = ({ doc }) => {
    const { Icon, color } = getFileIcon(doc);

    return (
        <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.02, boxShadow: '0 8px 15px rgba(0, 0, 0, 0.1)' }}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border-l-4 border-indigo-400 hover:border-indigo-600 transition-all duration-200"
        >
            <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-full ${color} dark:bg-gray-700`}>
                    <Icon size={20} />
                </div>
                <div>
                    <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{doc.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {doc.category} | Last updated: {doc.date}
                    </p>
                </div>
            </div>

            <div className="flex items-center space-x-3">
                {doc.required && (
                    <span 
                        className={`text-xs font-semibold px-2 py-1 rounded-full ${doc.status === 'Acknowledgement Due' ? 'bg-yellow-100 text-yellow-800' : doc.status === 'Pending Signature' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}
                        title={`Status: ${doc.status}`}
                    >
                        {doc.status === 'Acknowledgement Due' ? 'Action Required' : doc.status}
                    </span>
                )}
                <motion.button 
                    whileHover={{ scale: 1.1 }} 
                    whileTap={{ scale: 0.9 }} 
                    onClick={() => alert(`Viewing document: ${doc.name}`)}
                    className="p-2 rounded-full text-indigo-600 hover:bg-indigo-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label={`View ${doc.name}`}
                >
                    <FileText size={20} />
                </motion.button>
                <motion.button 
                    whileHover={{ scale: 1.1 }} 
                    whileTap={{ scale: 0.9 }} 
                    onClick={() => alert(`Downloading document: ${doc.name}`)}
                    className="p-2 rounded-full text-green-600 hover:bg-green-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label={`Download ${doc.name}`}
                >
                    <Download size={20} />
                </motion.button>
            </div>
        </motion.div>
    );
};

// --- UTILITY COMPONENT: ProgressTracker ---
const ProgressTracker = ({ documents }) => {
    const requiredDocs = documents.filter(d => d.required);
    const completedDocs = requiredDocs.filter(d => d.status !== 'Acknowledgement Due' && d.status !== 'Pending Signature');
    
    const totalRequired = requiredDocs.length;
    const totalCompleted = completedDocs.length;
    const progressPercent = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 100;

    return (
        <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 bg-indigo-600 dark:bg-indigo-900 text-white rounded-xl shadow-lg mb-8"
        >
            <h2 className="text-xl font-bold mb-3 flex items-center">
                <CheckCircle size={24} className="mr-2 text-green-300" />
                Required Document Status
            </h2>
            <p className="text-sm mb-3">
                You have completed **{totalCompleted}** of **{totalRequired}** required onboarding/compliance tasks.
            </p>
            <div className="w-full bg-indigo-800 rounded-full h-3">
                <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1.5 }}
                    className="bg-green-400 h-3 rounded-full"
                    style={{ width: `${progressPercent}%` }}
                ></motion.div>
            </div>
            <p className="mt-2 text-right text-sm font-semibold">{progressPercent}% Complete</p>
        </motion.div>
    );
};

// --- MAIN COMPONENT: EmployeeDocuments ---
const EmployeeDocuments = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCategory, setFilterCategory] = useState('All');

    useEffect(() => {
        fetchDocuments().then(data => {
            setDocuments(data.data);
            setLoading(false);
        });
    }, []);

    const categories = useMemo(() => {
        const all = documents.map(d => d.category);
        return ['All', ...new Set(all)].sort();
    }, [documents]);

    const filteredDocuments = useMemo(() => {
        if (filterCategory === 'All') return documents;
        return documents.filter(d => d.category === filterCategory);
    }, [documents, filterCategory]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 text-indigo-600">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                    <FileText size={48} />
                </motion.div>
                <p className="mt-4 text-xl font-medium ml-4">Fetching Secure Documents...</p>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-8 md:p-12 min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-500">
            
            {/* Header */}
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="mb-8 border-b-4 border-purple-500 pb-4"
            >
                <h1 className="text-4xl font-extrabold text-purple-800 dark:text-purple-400 tracking-tight flex items-center">
                    <Lock size={30} className="mr-3" /> Employee Documents
                </h1>
                <p className="mt-1 text-lg text-gray-500 dark:text-gray-400">
                    Your central repository for contracts, tax forms, and company policies.
                </p>
            </motion.header>

            {/* Progress Tracker (Actionable Insight) */}
            <ProgressTracker documents={documents} />

            {/* Filters and Controls */}
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 w-full sm:w-auto">
                    <Folder size={20} className="text-purple-500 ml-1" />
                    <label htmlFor="category-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden md:block">Category:</label>
                    <div className="relative w-full">
                        <select
                            id="category-filter"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="appearance-none bg-transparent py-1 pl-3 pr-8 text-sm font-medium text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-md w-full"
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat} ({cat !== 'All' ? documents.filter(d => d.category === cat).length : documents.length})</option>
                            ))}
                        </select>
                        <AlertTriangle size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-purple-500" />
                    </div>
                </div>
                {/* Additional upload button could go here */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="py-2 px-4 text-sm font-bold bg-green-500 text-white rounded-lg shadow-md hover:bg-green-600 transition-colors w-full sm:w-auto"
                >
                    <Download size={20} className="inline mr-2" /> Download All
                </motion.button>
            </div>

            {/* Document List */}
            <motion.div
                variants={listVariants}
                initial="hidden"
                animate="visible"
                className="space-y-4"
            >
                <AnimatePresence>
                    {filteredDocuments.length > 0 ? (
                        filteredDocuments.map(doc => (
                            <DocumentCard key={doc.id} doc={doc} />
                        ))
                    ) : (
                        <motion.div 
                            key="no-docs"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="p-12 text-center bg-white dark:bg-gray-800 rounded-xl shadow-lg mt-10"
                        >
                            <File size={48} className="mx-auto text-gray-400 mb-4" />
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">No Documents Found</h2>
                            <p className="text-gray-500 dark:text-gray-400">Try selecting 'All' categories or contact HR.</p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default EmployeeDocuments;